import { IOgqlColumn } from '@opengov/ogql-types';
import {
  createAgGridColumnConfig,
  createReportConfiguration,
  IColumnAlias,
  IDatasetLensColumn,
  IReportConfiguration,
  IReportConfigurationDimensionalQuery,
  isDisplayDelegate
} from '@opengov/reporting-types';
import {
  getColumnName,
  getDisplayName,
  IDimensionalReportColumn,
  IReportColumn,
  mapColumnToFactory,
  isFYFPColumn
} from '@opengov/reporting-types/src/reportColumns-immer';
import { produce } from 'immer';

import { columnTypeToFactory, lensColumnToStorageReportColumn } from './columns';

const updateColumn = (
  reportConfigColumn: IReportColumn | IDimensionalReportColumn,
  dataMgrColumn: IOgqlColumn | IDatasetLensColumn,
  presentationConfigurationColumns: IColumnAlias[]
) => {
  if (!reportConfigColumn) return;
  const columnDisplayName = getDisplayName(reportConfigColumn, presentationConfigurationColumns, dataMgrColumn);
  if (!dataMgrColumn) {
    return produce(reportConfigColumn, (column) => {
      column.displayName = columnDisplayName;
    });
  }
  const dmCol = dataMgrColumn as any;
  const hidden =
    (dmCol.metadata && dmCol.metadata.tags && dmCol.metadata.tags.hidden === true) ||
    dmCol.hidden === true ||
    dmCol.visible === false;

  return produce(reportConfigColumn, (column) => {
    column.displayName = columnDisplayName;
    column.hidden = hidden;
    // not all column types have metadata
    if (column?.metadata?.tags) {
      column.metadata.tags.hidden = hidden;
    }
  });
};

export const identifyUsedColumns = (reportConfig: IReportConfiguration, columnNames: string[]): string[] => {
  const { detailTableQuery, presentationConfiguration, visualizationQuery } = reportConfig;
  return columnNames.filter(
    (columnName) =>
      detailTableQuery.sortColumn === columnName ||
      detailTableQuery.groupByColumns?.includes(columnName) ||
      detailTableQuery.filter.some((filter) => filter.columnName === columnName) ||
      detailTableQuery.filterBooleanOperators.some((filterOp) => filterOp.columnName === columnName) ||
      presentationConfiguration.agGridColumnConfig?.some(
        (col) => col.columnName === columnName && (col.hide || col.pinned)
      ) ||
      visualizationQuery.categorizedByColumn.name === columnName ||
      visualizationQuery.organizedBy.column?.name === columnName ||
      visualizationQuery.organizedBy.selection === columnName ||
      visualizationQuery.showColumn?.name === columnName ||
      visualizationQuery.filter.some((filter) => filter.columnName === columnName) ||
      visualizationQuery.filterBooleanOperators.some((filterOp) => filterOp.columnName === columnName) ||
      visualizationQuery.hiddenControls.some((col) => col.columnName === columnName)
  );
};

const handleDuplicateDisplayNamesFromDisplayDelegate = (
  reportConfig: IReportConfiguration,
  updatedCols: IReportColumn[]
) => {
  const displayDelegate: IDimensionalReportColumn = updatedCols.find(
    (col): col is IDimensionalReportColumn =>
      (col as IDimensionalReportColumn).joinColumnName && col.semanticTypes.some(isDisplayDelegate)
  );

  if (!displayDelegate) {
    // There are no dimensional display delegates in the column list. Return column list as-is.
    return updatedCols;
  }

  const duplicateDisplayNameCols = updatedCols.filter((col) => col.displayName === displayDelegate.displayName);

  let columnToReplace = duplicateDisplayNameCols.find((col) => col.name === displayDelegate.joinColumnName);

  if (columnToReplace === undefined && displayDelegate.joinColumnName === '__og_account_number_id') {
    columnToReplace = duplicateDisplayNameCols.find((col) => col.name === '__og_account_number');
  }

  if (!columnToReplace) {
    // The dimensional display delegate does not duplicate any other column display names. Return column list as-is.
    return updatedCols;
  }

  // The display delegate does duplicate the display label for the lens's join column. Examine the report configuration
  // to see if either column is explicitly used in a way that would break the report if the column was removed.
  const usedColumns = identifyUsedColumns(reportConfig, [displayDelegate.name, columnToReplace.name]);

  if (!usedColumns.includes(columnToReplace.name)) {
    updatedCols = updatedCols.filter((col) => col !== columnToReplace);
  } else if (!usedColumns.includes(displayDelegate.name)) {
    updatedCols = updatedCols.filter((col) => col != displayDelegate);
  } else {
    // Both are used, so do nothing
  }

  return updatedCols;
};

export const getCorrespondingDataMgrCol = (
  col: IReportColumn,
  lensColsFromDataMgr: IOgqlColumn[] | IDatasetLensColumn[]
) => {
  const columnName = isFYFPColumn(col) ? (col as IDimensionalReportColumn).joinColumnName : col.name;

  return (lensColsFromDataMgr as (IOgqlColumn | IDatasetLensColumn)[]).find((dmCol) => columnName === dmCol.name);
};

export const applyCurrentMetadata = (
  reportConfig: IReportConfiguration,
  lensColsFromDataMgr: IOgqlColumn[] | IDatasetLensColumn[],
  isLensBased: boolean,
  lensAndJoinedColumns?: IReportColumn[] | IDimensionalReportColumn[]
): IReportConfiguration => {
  if (!reportConfig || !reportConfig.reportId) {
    return reportConfig;
  }

  const rc = reportConfig;
  const presentationConfigurationColumns = rc.presentationConfiguration.columnNames;
  const columnList = lensAndJoinedColumns ? lensAndJoinedColumns : rc.detailTableQuery.columns;

  // detailTableQuery might not have all the columns...  mostly standard templates
  // will not have non-standard schema columns
  let updatedCols = columnList.map((col) => {
    const dataMgrCol = getCorrespondingDataMgrCol(col, lensColsFromDataMgr);
    return updateColumn(col, dataMgrCol, presentationConfigurationColumns);
  });

  // append extra columns at the end of the list
  lensColsFromDataMgr.forEach((col) => {
    if (!updatedCols.find((c) => c.name === col.name)) {
      const column = isLensBased ? lensColumnToStorageReportColumn(col) : columnTypeToFactory(col);
      updatedCols.push(column as any);
    }
  });

  updatedCols = handleDuplicateDisplayNamesFromDisplayDelegate(reportConfig, updatedCols);

  const updatedCatByCol = updateColumn(
    mapColumnToFactory(rc.visualizationQuery.categorizedByColumn),
    getCorrespondingDataMgrCol(rc.visualizationQuery.categorizedByColumn, lensColsFromDataMgr),
    presentationConfigurationColumns
  );
  const updatedShowCol = updateColumn(
    mapColumnToFactory(rc.visualizationQuery.showColumn),
    getCorrespondingDataMgrCol(rc.visualizationQuery.showColumn, lensColsFromDataMgr),
    presentationConfigurationColumns
  );
  const updatedOrgByCol = updateColumn(
    rc.visualizationQuery.organizedBy.column,
    getCorrespondingDataMgrCol(rc.visualizationQuery.organizedBy.column, lensColsFromDataMgr),
    presentationConfigurationColumns
  );

  const dimensionalSummaryQueries = rc.dimensionalSummaryQueries?.map((query) =>
    produce(query, (draftQuery) => {
      const updatedColumnsCol = updateColumn(
        mapColumnToFactory(draftQuery.dimension.columnsColumn),
        getCorrespondingDataMgrCol(draftQuery.dimension.columnsColumn, lensColsFromDataMgr),
        presentationConfigurationColumns
      );
      const updatedRowsCol = updateColumn(
        mapColumnToFactory(draftQuery.dimension.rowsColumn),
        getCorrespondingDataMgrCol(draftQuery.dimension.rowsColumn, lensColsFromDataMgr),
        presentationConfigurationColumns
      );

      if (updatedColumnsCol) {
        draftQuery.dimension.columnsColumn = updatedColumnsCol as IReportConfigurationDimensionalQuery['columnsColumn'];
      }
      if (updatedRowsCol) {
        draftQuery.dimension.rowsColumn = updatedRowsCol as IReportConfigurationDimensionalQuery['rowsColumn'];
      }
    })
  );

  const agGridConfig = rc.presentationConfiguration.agGridColumnConfig
    ? updatedCols
        .map((col, i) => {
          if (col.hidden) {
            return;
          }
          const existingConfig =
            rc.presentationConfiguration.agGridColumnConfig.find((c) => c.columnName === col.name) ||
            rc.presentationConfiguration.agGridColumnConfig.find((c) => c.columnName === getColumnName(col.name));
          return createAgGridColumnConfig({
            sortedIndex: existingConfig ? existingConfig.sortedIndex : updatedCols.length + i,
            columnName: col.name,
            pinned: (existingConfig && existingConfig.pinned) || null,
            hide: (existingConfig && existingConfig.hide) || false
          });
        })
        .filter((col) => col !== undefined)
    : null;

  return createReportConfiguration(
    produce(reportConfig, (draftConfig) => {
      draftConfig.dimensionalSummaryQueries = dimensionalSummaryQueries;
      draftConfig.detailTableQuery.columns = updatedCols;
      draftConfig.visualizationQuery.categorizedByColumn = updatedCatByCol;
      draftConfig.visualizationQuery.showColumn = updatedShowCol as any;
      draftConfig.visualizationQuery.organizedBy.column = updatedOrgByCol;
      draftConfig.presentationConfiguration.agGridColumnConfig = agGridConfig;
    })
  );
};

export const applyMetadataForNonCoaReportConfig = (reportConfig: IReportConfiguration): IReportConfiguration => {
  const presentationConfigurationColumns = reportConfig.presentationConfiguration.columnNames;

  return produce(reportConfig, (draftConfig) => {
    // Updating the display name of the show column to the display name of the column in the presentation configuration
    // This is necessary for summary table embeds to display the correct column name
    if (draftConfig.visualizationQuery?.showColumn) {
      const column = presentationConfigurationColumns.find(
        (c) => c.originalName === draftConfig.visualizationQuery.showColumn.name
      );
      if (column) {
        draftConfig.visualizationQuery.showColumn.displayName = column.displayName;
      }
    }
  });
};
